import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileText, Send } from "lucide-react";
import { ujmsOrderBgReport, ujmsBgOrders, ujmsOpenBgOrderPdf } from "../../services/ujmsApi";
import { Panel, Field, inputCls, btnPrimary, btnGhost, thCls, tdCls, fmtDT } from "../ui";

export default function IspBackgroundOrder() {
  const [f, setF] = useState({ name: "", dob: "", sid: "", purpose: "Official justice-agency background report" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { ujmsBgOrders().then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.sid.trim() && !(f.name.trim() && f.dob.trim())) { toast.error("Enter a SID or a name + date of birth"); return; }
    setBusy(true);
    try { const r = await ujmsOrderBgReport(f); toast.success(`ISP fulfilled — ${r.data.check_number}`); setF({ ...f, name: "", dob: "", sid: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "The ISP could not fulfill the request"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="isp-bg-order-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#0b2d52]" /> ISP Background Report</h1>
        <p className="text-sm text-slate-500">Order a criminal-history background report from the Illinois State Police — fulfilled automatically, free of charge, and returned dated &amp; timestamped.</p>
      </div>
      <Panel title="Order a report" testId="isp-bg-order-form">
        <div className="px-3 py-2 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Full name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="isp-bg-name" /></Field>
          <Field label="Date of birth"><input type="date" className={inputCls} value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="isp-bg-dob" /></Field>
          <Field label="SID (optional)"><input className={inputCls} value={f.sid} onChange={(e) => setF({ ...f, sid: e.target.value })} data-testid="isp-bg-sid" /></Field>
          <Field label="Purpose"><input className={inputCls} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="isp-bg-purpose" /></Field>
        </div>
        <div className="px-3 pb-3"><button className={btnPrimary} onClick={submit} disabled={busy} data-testid="isp-bg-submit"><Send className="w-4 h-4" /> {busy ? "Requesting…" : "Request report from ISP"}</button></div>
      </Panel>
      <Panel title="My department's reports" testId="isp-bg-orders">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Fulfilled</th><th className={thCls}>Subject</th><th className={thCls}>Check #</th><th className={thCls}>Eligible</th><th className={thCls}>Pages</th><th className={thCls}>Ordered by</th><th className={thCls}></th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50" data-testid={`isp-bg-row-${o.id}`}>
                <td className={`${tdCls} font-mono text-xs`}>{fmtDT(o.fulfilled_at)}</td>
                <td className={tdCls}>{(o.subject || {}).name || (o.subject || {}).sid || "—"}<div className="text-[10px] text-slate-500">{(o.subject || {}).dob || ""}</div></td>
                <td className={`${tdCls} font-mono text-xs`}>{o.check_number}</td>
                <td className={tdCls}><span className="text-amber-800 text-xs font-semibold" data-testid={`court-bgc-incomplete-${o.id}`}>External checks incomplete</span></td>
                <td className={`${tdCls} text-xs`}>{o.pages}</td>
                <td className={`${tdCls} text-xs`}>{o.requested_by}</td>
                <td className={tdCls}><button className={btnGhost} onClick={() => ujmsOpenBgOrderPdf(o.id, `ISP Background — ${o.check_number}`)} data-testid={`isp-bg-pdf-${o.id}`}><FileText className="w-3.5 h-3.5" /> Report</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className={tdCls}>No reports ordered yet.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
